import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { loadRegionMap, loadStats } from '../data/load';
import type { RegionMap, Stats } from '../data/types';
import { Seo } from '../components/Seo';
import { useBookData } from '../state/AppData';
import { IconAbierto, IconAula, IconExplorar, IconLeer, IconPatrimonio, IconRetos } from '../components/icons';

const n = (v: number) => v.toLocaleString('es-ES');

/** Los identificadores de licencia del manifiesto, escritos como se leen. */
const LICENSE: Record<string, string> = {
  'CC-BY-4.0': 'CC BY 4.0', 'CC0-1.0': 'CC0 1.0', 'ODbL-1.0': 'ODbL 1.0', 'public-domain': 'Dominio público',
};
const licence = (id: string) => LICENSE[id] ?? id;
const fecha = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });

/** Portada del proyecto. No es la portada de una ciudad: es la puerta de las seis colecciones, y
 *  está escrita para que se entienda en diez segundos qué es esto, por qué es didáctico, de qué
 *  datos abiertos vive y qué tiene de nuevo. Las cifras no están escritas aquí: las cuenta el ETL
 *  (`stats.json`) y la silueta de la comunidad viene de OpenStreetMap (`region-map.json`), de modo
 *  que la página no puede quedarse diciendo algo que ya no sea verdad. */
export function Landing() {
  const { cities } = useBookData();
  const [stats, setStats] = useState<Stats | null>(null);
  const [map, setMap] = useState<RegionMap | null>(null);
  useEffect(() => {
    loadStats().then(setStats).catch(() => setStats(null));
    loadRegionMap().then(setMap).catch(() => setMap(null));
  }, []);

  // las tres puertas, en el orden del libro; de cada provincia salen su capital y su provincia
  const doors = ['Salamanca', 'Ávila', 'Segovia'].map((province) => ({
    province,
    capital: cities.find((c) => c.province === province && c.level === 'capital'),
    provincia: cities.find((c) => c.province === province && c.level === 'provincia'),
  }));
  const jcyl = stats?.datasets.find((d) => d.kind === 'jcyl');

  return (
    <div className="landing">
      <Seo />
      <a className="landing__skip" href="#que-es">Saltar al contenido</a>

      <header className="landing__hero">
        <div className="landing__hero-text">
          <p className="landing__eyebrow">Recurso educativo abierto</p>
          <p className="landing__wordmark" aria-hidden="true">1884<span>.es</span></p>
          <h1>Explora la Castilla y León de 1884</h1>
          <p className="landing__claim">Un viaje en el tiempo por el patrimonio de Castilla y León con la obra de José María Quadrado y los datos abiertos de la Junta de CyL.</p>
          <p className="landing__lede">
            Historia, literatura y patrimonio se unen a los datos abiertos para crear una experiencia
            educativa interactiva. Lee el libro que en 1884 describió, monumento a monumento, el
            patrimonio de Salamanca, Ávila y Segovia; sitúa cada lugar sobre el mapa de hoy, descubre
            la historia de la región y comprueba, con fuentes, qué se conserva y qué ha desaparecido.
          </p>
          <ul className="landing__cta">
            <li><Link className="btn btn--cta btn--lg" to="/ciudad?ciudad=salamanca">Entrar en 1884</Link></li>
            <li><Link className="btn btn--lg" to="/aula">Ver la propuesta didáctica →</Link></li>
          </ul>
          <p className="landing__badge">Basado en datos abiertos de la Junta de Castilla y León</p>
          <p className="landing__aula">
            <span>Diseñado para el aula</span>
            <span className="landing__materias">
              {['Historia', 'Geografía', 'Literatura', 'Patrimonio', 'Competencia digital'].map((m) => (
                <span key={m}>{m}</span>
              ))}
            </span>
          </p>
        </div>
        <div className="landing__hero-map">{map && <RegionMapFigure map={map} />}</div>
      </header>

      <section className="landing__why" aria-labelledby="t-por-que">
        <h2 id="t-por-que">Una obra literaria de 1884 convertida en un recurso educativo basado en datos abiertos</h2>
        <ul className="landing__checks">
          <li><b>Utilidad didáctica.</b> Guía docente, {stats?.sequences ?? 12} secuencias temporizadas con ficha de
            alumnado y rúbricas, {stats?.activities ?? 38} retos y {stats?.itineraries ?? 18} itinerarios. Para Secundaria,
            Bachillerato y primeros cursos universitarios; adaptable a 6.º de Primaria.</li>
          <li><b>Innovación.</b> El texto de 1884 y el mapa de hoy van sincronizados: cada pasaje lleva a su lugar
            y cada lugar, a todos sus pasajes. El alumnado investiga sobre fuentes, no sobre un resumen.</li>
          <li><b>Conjuntos de datos.</b> «Relación de monumentos» del Portal de Datos Abiertos de la Junta, cruzada
            con otras cinco fuentes públicas. Cada dato lleva su origen, su licencia y su grado de certeza.</li>
          <li><b>Facilidad de uso y accesibilidad.</b> Sin cuentas, sin cookies y sin registro. Se maneja con
            teclado, se puede leer sin abrir el mapa y se verifica con axe en 49 rutas.</li>
          <li><b>Calidad técnica.</b> Proceso reproducible de principio a fin, fuentes congeladas con su hash,
            licencias declaradas y 123 pruebas automáticas en cada versión.</li>
        </ul>
      </section>

      <section id="que-es" className="landing__section landing__learn" aria-labelledby="t-aprender" tabIndex={-1}>
        <h2 id="t-aprender">¿Qué vas a hacer aquí?</h2>
        <ul className="landing__three landing__three--four">
          <li>
            <IconLeer className="landing__icon" />
            <h3>Lee la obra de 1884</h3>
            <p>El texto íntegro, en {n(stats?.words ?? 0)} palabras, con su página original al lado. Los lugares aparecen subrayados dentro del relato.</p>
          </li>
          <li>
            <IconExplorar className="landing__icon" />
            <h3>Explora cada lugar sobre el mapa actual</h3>
            <p>Cada iglesia, puerta, plaza o castillo que el libro nombra, situado sobre la cartografía de hoy.</p>
          </li>
          <li>
            <IconPatrimonio className="landing__icon" />
            <h3>Descubre qué patrimonio se conserva</h3>
            <p>Con el catálogo de la Junta al lado del texto: lo que sigue en pie, lo que cambió y lo que ninguna fuente documenta.</p>
          </li>
          <li>
            <IconAula className="landing__icon" />
            <h3>Aprende con retos y actividades</h3>
            <p>{stats?.activities ?? ''} retos, {stats?.itineraries ?? ''} itinerarios y {stats?.sequences ?? ''} secuencias de aula, con pista, explicación y evidencia enlazada.</p>
          </li>
        </ul>
        <p className="landing__note">
          El alumnado no se limita a leer sobre una ciudad: la recorre, localiza los lugares descritos,
          consulta las fuentes, las compara y resuelve actividades sobre el territorio.
        </p>
      </section>

      <section className="landing__section landing__cities" aria-labelledby="t-ciudades">
        <h2 id="t-ciudades">Tres ciudades, tres provincias. Tres puertas de entrada a la Castilla y León de 1884.</h2>
        <p className="landing__sub">
          Quadrado recorrió Salamanca, Ávila y Segovia y escribió lo que vio: las tres capitales y los
          pueblos de sus provincias. La edición mantiene esa división, y se entra por donde se quiera.
        </p>
        <ul className="landing__doors">
          {doors.map((d) => (
            <li key={d.province}>
              <h3>{d.province}</h3>
              <p>{DOOR_TEXT[d.province]}</p>
              <p className="landing__door-links">
                {d.capital && <Link className="btn" to={`/explorar?ciudad=${d.capital.slug}`}>Explorar la capital →</Link>}
                {d.provincia && <Link className="btn" to={`/explorar?ciudad=${d.provincia.slug}`}>Y su provincia →</Link>}
              </p>
            </li>
          ))}
        </ul>
      </section>

      {stats && (
        <section className="landing__section landing__data" aria-labelledby="t-datos">
          <h2 id="t-datos">Detrás de cada lugar hay datos verificables</h2>
          <p className="landing__kicker">Historia construida con datos</p>
          <p className="landing__sub">
            El punto de partida es <a href={jcyl?.url} target="_blank" rel="noopener noreferrer">«{jcyl?.title}»</a>,
            del Portal de Datos Abiertos de la Junta de Castilla y León ({n(jcyl?.records ?? 0)} registros, {licence(jcyl?.license ?? '')}),
            cruzado con otras cinco fuentes públicas. Cada afirmación de la aplicación lleva la suya.
          </p>
          <ul className="landing__figures">
            <li><strong>{stats.provinces}</strong><span>provincias, {stats.municipalities} municipios</span></li>
            <li><strong>{n(stats.places)}</strong><span>lugares revisados uno a uno</span></li>
            <li><strong>{n(stats.placesWithRecord)}</strong><span>enlazados al catálogo de la Junta</span></li>
            <li><strong>{n(stats.mentions)}</strong><span>menciones ancladas al texto</span></li>
            <li><strong>{stats.itineraries + stats.activities + stats.sequences}</strong><span>actividades: {stats.itineraries} itinerarios, {stats.activities} retos y {stats.sequences} secuencias de aula</span></li>
            <li><strong>{n(stats.facsimileLeaves)}</strong><span>hojas del ejemplar, en facsímil</span></li>
          </ul>
          <table className="landing__sources">
            <caption>Las seis fuentes, con su licencia y para qué sirve cada una</caption>
            <thead><tr><th scope="col">Fuente</th><th scope="col">Para qué</th><th scope="col">Licencia</th></tr></thead>
            <tbody>
              {stats.datasets.map((d) => (
                <tr key={d.id} className={d.kind === 'jcyl' ? 'is-jcyl' : undefined}>
                  <th scope="row">
                    {d.url ? <a href={d.url} target="_blank" rel="noopener noreferrer">{d.title}</a> : d.title}
                    <span className="landing__pub">{d.publisher}</span>
                  </th>
                  <td>{d.role}</td>
                  <td>{licence(d.license)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="landing__note">
            {n(stats.placesUndetermined)} de los {n(stats.places)} lugares quedan como «no determinado»: ninguna
            fuente disponible documenta qué fue de ellos. No se rellena el hueco con suposiciones; se señala,
            y se convierte en la pregunta que el alumnado investiga.
          </p>
        </section>
      )}

      <section className="landing__section landing__shot" aria-labelledby="t-aula">
        <div className="landing__shot-text">
          <h2 id="t-aula">Una ciudad convertida en aula</h2>
          <p>
            A la izquierda, el texto de 1884 con los lugares subrayados. En el centro, el mapa de hoy.
            A la derecha, la ficha del lugar: lo que dice Quadrado, lo que dice el catálogo de la Junta y
            lo que interpreta esta edición, cada cosa con su etiqueta y su fuente. Pulsar un lugar del
            texto lo destaca en el mapa; pulsar un punto del mapa lleva al pasaje del libro.
          </p>
          <p><Link className="btn btn--cta" to="/leer">Entrar en el lector →</Link></p>
        </div>
        <figure>
          <img src={`${import.meta.env.BASE_URL}img/lector.webp`} width={2880} height={1800} loading="lazy"
               alt="La vista de lectura: a la izquierda el texto de 1884 con los lugares subrayados, en el centro el mapa con los marcadores y a la derecha la ficha de un monumento con sus fuentes." />
          <figcaption>Vista de lectura de Salamanca, con la casa de las Conchas seleccionada.</figcaption>
        </figure>
      </section>

      <section className="landing__section landing__class" aria-labelledby="t-clase">
        <h2 id="t-clase">Listo para usar en el aula</h2>
        <ul className="landing__three">
          <li>
            <IconRetos className="landing__icon" />
            <h3>Para el alumnado</h3>
            <p>Exploración autónoma, mapas, itinerarios y {stats?.activities ?? ''} retos con pista, explicación y evidencia enlazada. Sin cuentas y sin puntuaciones.</p>
          </li>
          <li>
            <IconAula className="landing__icon" />
            <h3>Para el profesorado</h3>
            <p>Guía docente, {stats?.sequences ?? ''} secuencias temporizadas con ficha de alumnado, rúbricas de evaluación y solucionario razonado, todo imprimible.</p>
          </li>
          <li>
            <IconAbierto className="landing__icon" />
            <h3>Abierto de verdad</h3>
            <p>Textos y materiales en CC BY 4.0, datos derivados en ODbL 1.0 y código MIT. Se puede copiar, adaptar y reutilizar citando la procedencia.</p>
          </li>
        </ul>
        <p className="landing__cta-final">
          <Link className="btn btn--primary btn--lg" to="/aula">Ver la propuesta didáctica</Link>
          <Link className="btn btn--lg" to="/proyecto/fuentes">Fuentes y método</Link>
        </p>
      </section>

      <footer className="landing__footer">
        <p>
          Texto: José María Quadrado, <cite>Salamanca, Ávila y Segovia</cite> (1884), dominio público.
          Datos monumentales: Junta de Castilla y León, CC BY 4.0. Ubicaciones y límites: © colaboradores de
          OpenStreetMap, ODbL 1.0. Cartografía: Instituto Geográfico Nacional, CC BY 4.0.
          Edición y materiales didácticos: Eugenio Serrano, CC BY 4.0.
        </p>
        <p>
          <Link to="/proyecto/fuentes">Fuentes, método y créditos</Link> · <Link to="/proyecto/aviso-legal">Aviso legal y privacidad</Link>
          {stats && <> · Datos de {fecha(stats.generated)}</>}
        </p>
      </footer>
    </div>
  );
}

const DOOR_TEXT: Record<string, string> = {
  Salamanca: 'La ciudad de las dos catedrales y la universidad, y una provincia que va de Ciudad Rodrigo a la sierra de Francia y a Alba de Tormes.',
  Ávila: 'La muralla que Quadrado describe entera, y una provincia de Gredos a Arévalo y Madrigal, la villa de las reinas.',
  Segovia: 'El acueducto, el alcázar y las iglesias románicas, y una provincia de reales sitios, castillos y villas amuralladas.',
};

/** Lo que cada ciudad pone en el viaje: no es decoración, es lo que el libro dedica a cada una. */
const HITO: Record<string, string> = {
  Salamanca: 'dos catedrales',
  'Ávila': 'la muralla',
  Segovia: 'el acueducto',
};

/** Las tres capitales caen muy juntas: cada rótulo se coloca por su lado para no pisarse. */
const ROTULO: Record<string, { dx: number; dy: number; anchor: 'start' | 'middle' | 'end' }> = {
  Salamanca: { dx: -24, dy: -4, anchor: 'end' },
  'Ávila': { dx: 0, dy: 52, anchor: 'middle' },
  Segovia: { dx: 24, dy: -4, anchor: 'start' },
};

/** La silueta de la comunidad con las tres provincias del libro encendidas y el viaje trazado en el
 *  orden en que Quadrado lo escribió: Salamanca, Ávila y Segovia son la primera, la segunda y la
 *  tercera parte del volumen. Es una imagen; el mapa de verdad, con sus teselas y sus marcadores,
 *  está en Explorar. */
function RegionMapFigure({ map }: { map: RegionMap }) {
  const pts = map.provinces.map((p) => p.city);
  // arco suave entre paradas: una recta parecería una frontera más, y esto es un recorrido
  const ruta = pts.slice(1).map((b, i) => {
    const a = pts[i];
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2 - Math.hypot(b.x - a.x, b.y - a.y) * 0.16;
    return `${i === 0 ? `M${a.x} ${a.y} ` : ''}Q${mx.toFixed(1)} ${my.toFixed(1)} ${b.x} ${b.y}`;
  }).join(' ');

  return (
    <figure className="regionmap">
      <svg viewBox={map.viewBox} role="img" aria-labelledby="mapa-titulo">
        <title id="mapa-titulo">
          Mapa de Castilla y León con las provincias de Salamanca, Ávila y Segovia destacadas y el
          recorrido del libro trazado entre sus tres capitales, en ese orden.
        </title>
        <path className="regionmap__region" d={map.region} />
        {map.provinces.map((p) => <path key={p.name} className="regionmap__prov" d={p.path} />)}
        <path className="regionmap__ruta" d={ruta} />
        {map.provinces.map((p, i) => (
          <g key={p.name} className="regionmap__city">
            <circle cx={p.city.x} cy={p.city.y} r={15} />
            <text className="regionmap__num" x={p.city.x} y={p.city.y + 6}>{i + 1}</text>
            <text className="regionmap__name" textAnchor={ROTULO[p.city.name].anchor}
                  x={p.city.x + ROTULO[p.city.name].dx} y={p.city.y + ROTULO[p.city.name].dy}>{p.city.name}</text>
            <text className="regionmap__hito" textAnchor={ROTULO[p.city.name].anchor}
                  x={p.city.x + ROTULO[p.city.name].dx} y={p.city.y + ROTULO[p.city.name].dy + 26}>{HITO[p.city.name]}</text>
          </g>
        ))}
      </svg>
      <figcaption>
        El orden del viaje es el del libro: primera, segunda y tercera parte.
        Límites administrativos de OpenStreetMap (ODbL 1.0).
      </figcaption>
    </figure>
  );
}
